import { ApiError, clientApi, getClientId } from '@/lib/api-client'

// ponytail: browser PUT needs S3 bucket CORS. Presigned GET preview uses the same CORS rule.
// Terraform owner: deploy/staging/terraform/kyc-docs.tf + deploy/staging/terraform/kyc-cors-nonprod.json
// Production: deploy/production/terraform/s3.tf (var kyc_docs_cors_allowed_origins; TODO confirm origins).
export type UploadKycErrorCode =
  | 'NO_CLIENT_ID'
  | 'S3_NETWORK'
  | 'S3_PUT_FAILED'
  | 'API'
  | 'INVALID_FORMAT'
  | 'INVALID_SIZE'
  | 'INVALID_DIMENSION'

export type KycFormatKey =
  | 'heic'
  | 'webp'
  | 'pdf'
  | 'gif'
  | 'bmp'
  | 'tiff'
  | 'avif'
  | 'svg'
  | 'video'
  | 'invalid_jpeg_png'
  | 'empty'
  | 'unknown'

export class UploadKycError extends Error {
  code: UploadKycErrorCode
  formatKey?: KycFormatKey

  constructor(code: UploadKycErrorCode, message?: string, formatKey?: KycFormatKey) {
    super(message ?? code)
    this.name = 'UploadKycError'
    this.code = code
    this.formatKey = formatKey
  }
}

const KYC_MAX_BYTES = 2 * 1024 * 1024
const KYC_MIN_PX = 600

const EXT_UNSUPPORTED: Record<string, KycFormatKey> = {
  heic: 'heic',
  heif: 'heic',
  webp: 'webp',
  pdf: 'pdf',
  gif: 'gif',
  bmp: 'bmp',
  tif: 'tiff',
  tiff: 'tiff',
  avif: 'avif',
  svg: 'svg',
  mov: 'video',
  mp4: 'video',
  m4v: 'video',
  webm: 'video',
}

const MIME_UNSUPPORTED: Record<string, KycFormatKey> = {
  'image/heic': 'heic',
  'image/heif': 'heic',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
  'image/gif': 'gif',
  'image/bmp': 'bmp',
  'image/x-ms-bmp': 'bmp',
  'image/tiff': 'tiff',
  'image/avif': 'avif',
  'image/svg+xml': 'svg',
}

function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  if (dot < 0) return ''
  return name.slice(dot + 1).toLowerCase()
}

function normalizeMime(file: File): string {
  return (file.type || '').split(';')[0]!.trim().toLowerCase()
}

function detectFromMagic(prefix: Uint8Array): KycFormatKey | 'jpeg' | 'png' {
  if (prefix.length === 0) return 'empty'
  const head = prefix
  if (head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46) return 'pdf'
  if (head[0] === 0x47 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x38) return 'gif'
  if (head[0] === 0x42 && head[1] === 0x4d) return 'bmp'
  if (
    head.length >= 8 &&
    head[0] === 0x89 &&
    head[1] === 0x50 &&
    head[2] === 0x4e &&
    head[3] === 0x47 &&
    head[4] === 0x0d &&
    head[5] === 0x0a &&
    head[6] === 0x1a &&
    head[7] === 0x0a
  ) {
    return 'png'
  }
  if (head.length >= 3 && head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return 'jpeg'
  if (
    head.length >= 12 &&
    head[0] === 0x52 &&
    head[1] === 0x49 &&
    head[2] === 0x46 &&
    head[3] === 0x46 &&
    head[8] === 0x57 &&
    head[9] === 0x45 &&
    head[10] === 0x42 &&
    head[11] === 0x50
  ) {
    return 'webp'
  }
  if (
    (head[0] === 0x49 && head[1] === 0x49 && head[2] === 0x2a && head[3] === 0x00) ||
    (head[0] === 0x4d && head[1] === 0x4d && head[2] === 0x00 && head[3] === 0x2a)
  ) {
    return 'tiff'
  }
  if (head.length >= 12 && head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70) {
    const brand = String.fromCharCode(head[8]!, head[9]!, head[10]!, head[11]!)
    if (['heic', 'heix', 'mif1', 'msf1'].includes(brand)) return 'heic'
    if (['avif', 'avis'].includes(brand)) return 'avif'
    if (['qt  ', 'mp42', 'isom', 'M4V ', '3gp4', 'avc1', 'mp41'].includes(brand)) return 'video'
  }
  const text = new TextDecoder().decode(head.slice(0, Math.min(head.length, 64))).trimStart()
  if (text.startsWith('<?xml') || text.startsWith('<svg') || text.startsWith('<SVG')) return 'svg'
  return 'unknown'
}

function declaredUnsupported(mime: string, ext: string): KycFormatKey | null {
  if (mime.startsWith('video/')) return 'video'
  if (mime in MIME_UNSUPPORTED) return MIME_UNSUPPORTED[mime]!
  if (ext in EXT_UNSUPPORTED) return EXT_UNSUPPORTED[ext]!
  return null
}

async function readFilePrefix(file: File, n = 16): Promise<Uint8Array> {
  const buf = await file.slice(0, n).arrayBuffer()
  return new Uint8Array(buf)
}

/** Detect KYC file format before upload. Returns jpeg/png when allowed. */
export async function detectKycFileFormat(file: File): Promise<KycFormatKey | 'jpeg' | 'png'> {
  if (file.size === 0) return 'empty'

  const mime = normalizeMime(file)
  const ext = fileExtension(file.name)
  const declared = declaredUnsupported(mime, ext)
  if (declared) return declared

  const prefix = await readFilePrefix(file)
  const magic = detectFromMagic(prefix)

  if (magic === 'jpeg' || magic === 'png') {
    const allowedMime =
      mime === 'image/jpeg' ||
      mime === 'image/png' ||
      mime === '' ||
      mime === 'application/octet-stream'
    const allowedExt = ext === '' || ext === 'jpg' || ext === 'jpeg' || ext === 'png'
    if (!allowedMime || !allowedExt) return 'invalid_jpeg_png'
    if (mime === 'image/png' && magic !== 'png') return 'invalid_jpeg_png'
    if (mime === 'image/jpeg' && magic !== 'jpeg') return 'invalid_jpeg_png'
    return magic
  }

  if (magic !== 'unknown') return magic

  if (mime === 'image/jpeg' || mime === 'image/png' || ext === 'jpg' || ext === 'jpeg' || ext === 'png') {
    return 'invalid_jpeg_png'
  }

  return 'unknown'
}

function apiFormatKey(err: ApiError): KycFormatKey | null {
  const detail = err.detail
  if (!detail || typeof detail !== 'object') return null
  const record = detail as { code?: string; detected?: string }
  if (record.code !== 'UNSUPPORTED_FILE_TYPE' || !record.detected) return null
  const key = record.detected as KycFormatKey
  return key
}

async function assertKycImage(file: File): Promise<'image/png' | 'image/jpeg'> {
  const format = await detectKycFileFormat(file)
  if (format !== 'jpeg' && format !== 'png') {
    throw new UploadKycError('INVALID_FORMAT', undefined, format)
  }
  if (file.size >= KYC_MAX_BYTES) throw new UploadKycError('INVALID_SIZE')
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    throw new UploadKycError('INVALID_FORMAT', undefined, 'invalid_jpeg_png')
  }
  const { width, height } = bitmap
  bitmap.close()
  if (width < KYC_MIN_PX || height < KYC_MIN_PX) throw new UploadKycError('INVALID_DIMENSION')
  return format === 'png' ? 'image/png' : 'image/jpeg'
}

export function kycFormatMessageKey(formatKey: KycFormatKey): string {
  return `form.uploadFormat.${formatKey}`
}

export async function uploadKycFile(
  docType: 'RECENT_PHOTO' | 'IDENTITY_CARD',
  file: File,
): Promise<{ filename: string; documentId: string }> {
  const clientId = getClientId()
  if (!clientId) throw new UploadKycError('NO_CLIENT_ID')

  const contentType = await assertKycImage(file)

  let meta
  try {
    meta = await clientApi.createUploadUrl(clientId, {
      doc_type: docType,
      filename: file.name,
      content_type: contentType,
      content_length: file.size,
    })
  } catch (err) {
    if (err instanceof ApiError) {
      const formatKey = apiFormatKey(err)
      if (formatKey) throw new UploadKycError('INVALID_FORMAT', undefined, formatKey)
      throw new UploadKycError('API', err.message)
    }
    throw new UploadKycError('API', err instanceof Error ? err.message : undefined)
  }

  let put: Response
  try {
    put = await fetch(meta.upload_url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    })
  } catch {
    throw new UploadKycError('S3_NETWORK')
  }

  if (!put.ok) {
    console.error(`S3_PUT_FAILED ${put.status}`)
    throw new UploadKycError('S3_PUT_FAILED')
  }

  try {
    await clientApi.confirmUpload(clientId, { document_id: meta.document_id, s3_key: meta.s3_key })
  } catch (err) {
    if (err instanceof ApiError) {
      const formatKey = apiFormatKey(err)
      if (formatKey) throw new UploadKycError('INVALID_FORMAT', undefined, formatKey)
      throw new UploadKycError('API', err.message)
    }
    throw new UploadKycError('API', err instanceof Error ? err.message : undefined)
  }

  return { filename: file.name, documentId: meta.document_id }
}
